/**
 * PostSessionModule
 *
 * Mandatory reflection form shown after leaving a Jitsi meeting.
 * Fields: key insight | concrete next action | deadline.
 *
 * If requirePostSession is enabled, navigation is blocked (via onBlock callback)
 * until the form is submitted. The parent decides how to handle blocking.
 */

import React, { useState, useCallback, memo } from 'react';
import {
  InsightIcon,
  GoalIcon,
  HourglassIcon,
  CheckIcon,
  SuccessIcon,
  WarningIcon,
  ICON_STYLES,
} from '../../components/icons';
import { Timestamp } from 'firebase/firestore';
import { savePostSessionReflectionWithCommitment } from './structuredGrowthService';
import type { StructuredGrowthEntityType } from './types';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface PostSessionModuleProps {
  entityId: string;
  entityType: StructuredGrowthEntityType;
  userId: string;
  userName?: string;
  meetingId: string;
  /**
   * When true, a warning is shown that this form must be submitted
   * before continuing. The parent component controls actual blocking.
   */
  blocking?: boolean;
  onSubmitted?: () => void;
}

// ---------------------------------------------------------------------------
// Field
// ---------------------------------------------------------------------------

const Field = memo(function Field({
  label,
  hint,
  icon,
  placeholder,
  value,
  onChange,
  rows = 2,
}: {
  label: string;
  hint?: string;
  icon: React.ReactNode;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
}) {
  return (
    <div className="space-y-1.5">
      <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700">
        {icon}
        {label}
      </label>
      {hint && <p className="text-xs text-gray-400 -mt-0.5">{hint}</p>}
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent resize-none text-gray-900 placeholder-gray-400"
      />
    </div>
  );
});

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const PostSessionModule: React.FC<PostSessionModuleProps> = ({
  entityId,
  userId,
  userName,
  meetingId,
  blocking = false,
  onSubmitted,
}) => {
  const [keyInsight, setKeyInsight] = useState('');
  const [nextAction, setNextAction] = useState('');
  const [deadlineDate, setDeadlineDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const isValid = keyInsight.trim() && nextAction.trim();

  const handleSubmit = useCallback(async () => {
    if (!isValid) return;
    setSaving(true);
    setSaveError(null);
    try {
      const deadlineTs = deadlineDate
        ? Timestamp.fromDate(new Date(deadlineDate))
        : null;

      await savePostSessionReflectionWithCommitment({
        entityId,
        userId,
        userName: userName || 'Lid',
        meetingId,
        keyInsight: keyInsight.trim(),
        concreteNextAction: nextAction.trim(),
        deadline: deadlineTs,
      });
      setDone(true);
      onSubmitted?.();
    } catch (error) {
      console.error('Post-session save failed:', error);
      setSaveError('Opslaan mislukt. Controleer je verbinding en probeer opnieuw.');
    } finally {
      setSaving(false);
    }
  }, [isValid, entityId, userId, userName, meetingId, keyInsight, nextAction, deadlineDate, onSubmitted]);

  if (done) {
    return (
      <div className="bg-emerald-50 rounded-xl border border-emerald-100 p-5 text-center">
        <SuccessIcon
          size={28}
          weight="fill"
          className="text-emerald-500 mx-auto mb-3"
        />
        <p className="text-sm font-semibold text-emerald-800 mb-1">
          Reflectie ingediend!
        </p>
        <p className="text-xs text-emerald-600">
          Je actie is opgeslagen. We zien je bij de volgende sessie.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      {/* Header */}
      <div className="flex items-center gap-2 mb-1">
        <InsightIcon size={16} weight="regular" className="text-gray-500" />
        <span className="text-sm font-semibold text-gray-900">Sessie reflectie</span>
      </div>
      <p className="text-xs text-gray-400 mb-5">
        Sluit de sessie af met een concreet inzicht en actie. Duurt minder dan 2 minuten.
      </p>

      {blocking && (
        <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-xl border border-amber-100 mb-4">
          <WarningIcon size={14} weight="fill" className="text-amber-500 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-700 leading-relaxed">
            Je moet deze reflectie indienen voordat je verder gaat. Dit helpt de groep groeien.
          </p>
        </div>
      )}

      <div className="space-y-4">
        <Field
          label="Grootste inzicht"
          hint="Wat neem je mee uit deze sessie?"
          icon={<InsightIcon size={13} weight="regular" className="text-purple-500" />}
          placeholder="Het belangrijkste dat ik heb geleerd of ingezien..."
          value={keyInsight}
          onChange={setKeyInsight}
          rows={3}
        />

        <Field
          label="Concrete volgende actie"
          hint="Wat ga je deze week daadwerkelijk doen?"
          icon={<GoalIcon size={13} weight="regular" className="text-blue-500" />}
          placeholder="Ik ga ... doen voor ..."
          value={nextAction}
          onChange={setNextAction}
        />

        {/* Deadline picker */}
        <div className="space-y-1.5">
          <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700">
            <HourglassIcon size={13} weight="regular" className="text-amber-500" />
            Deadline (optioneel)
          </label>
          <input
            type="date"
            value={deadlineDate}
            onChange={(e) => setDeadlineDate(e.target.value)}
            className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-gray-900"
          />
        </div>
      </div>

      {saveError && (
        <p className="mt-3 text-xs text-red-600">{saveError}</p>
      )}

      <button
        onClick={handleSubmit}
        disabled={!isValid || saving}
        className="mt-5 w-full h-10 bg-gray-900 text-white text-sm font-medium rounded-xl hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
      >
        <CheckIcon size={14} weight="bold" />
        <span>{saving ? 'Opslaan...' : 'Reflectie indienen'}</span>
      </button>
    </div>
  );
};

export default memo(PostSessionModule);
